/**
 * Agent Session Context - Clean React Implementation
 *
 * Provides a well-defined interface for agent session management.
 * This context uses pure React patterns with stable references for optimal performance.
 */

import { createContext } from 'react';

/**
 * Core data types for the agent system
 */

/**
 * @typedef {Object} AgentSession
 * @property {string} id - Frontend session ID (used in URLs)
 * @property {string} runtimeSessionId - Backend runtime session ID
 * @property {string} mcpSessionId - MCP session ID for adapter
 * @property {string} title - Session title (from first message)
 * @property {Date} createdAt - Creation timestamp
 * @property {Date} lastActivity - Last activity timestamp
 * @property {boolean} isActive - Whether session is currently active
 */

/**
 * @typedef {Object} ChatMessage
 * @property {string} id - Unique message ID
 * @property {string} sessionId - Associated session ID
 * @property {'user' | 'agent' | 'system'} type - Message type
 * @property {string} content - Message content
 * @property {Date} timestamp - Message timestamp
 * @property {'text' | 'markdown' | 'table'} contentType - Content format
 */

/**
 * @typedef {'idle' | 'thinking' | 'responding'} AgentState
 * Agent processing states:
 * - idle: Ready for new messages
 * - thinking: Processing user input, deciding what to do
 * - responding: Generating response text
 */

/**
 * @typedef {'connected' | 'connecting' | 'disconnected'} ConnectionStatus
 * Real-time connection status with adapter/backend
 */

/**
 * Context value interface - this is what consumers receive
 *
 * @typedef {Object} AgentSessionContextValue
 *
 * // ============ STATE (Read-only) ============
 * @property {Map<string, AgentSession>} sessions - All sessions
 * @property {Map<string, ChatMessage[]>} messages - Messages by session ID
 * @property {string | null} currentSessionId - Active session ID
 * @property {AgentSession | null} currentSession - Active session object
 * @property {ChatMessage[]} currentMessages - Messages for active session
 * @property {AgentState} agentState - Current agent processing state
 * @property {ConnectionStatus} connectionStatus - Real-time connection status
 * @property {boolean} isLoading - Loading session/creating session
 * @property {boolean} isSending - Sending message in progress
 * @property {string | null} error - Current error message
 *
 * // ============ ACTIONS (Stable references) ============
 * @property {function(string): Promise<AgentSession>} createSession - Create session with initial message
 * @property {function(string): Promise<void>} loadSession - Load/activate session by ID
 * @property {function(string): Promise<void>} sendMessage - Send message to current session
 * @property {function(): void} clearError - Clear current error
 *
 * // ============ QUERIES (Stable references) ============
 * @property {function(string): AgentSession | null} getSession - Get session by ID
 * @property {function(string): ChatMessage[]} getMessages - Get messages for session
 * @property {function(): AgentSession[]} getRecentSessions - Get recent sessions list
 */

/**
 * Agent Session Context
 *
 * Usage:
 * ```javascript
 * const context = useContext(AgentSessionContext);
 * if (!context) {
 *   throw new Error('Must be used within AgentSessionProvider');
 * }
 * ```
 *
 * @type {React.Context<AgentSessionContextValue | null>}
 */
const AgentSessionContext = createContext(null);

AgentSessionContext.displayName = 'AgentSessionContext';

export default AgentSessionContext;